import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useWorkspace } from '../context/WorkspaceContext';
import { useTheme } from '../context/ThemeContext';
import { getAllSidebarItems, getActiveWorkspaces } from '../config/workspaces';
import usePipelineDefinition from '../hooks/usePipelineDefinition';
import { dealStageMeta } from '../lib/dealStages';
import {
    searchPages, searchActions, searchRecords, buildResults, highlight,
} from './paletteSearch';
import './CommandPalette.css';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const RECENTS_KEY = 'crm_palette_recents';
const RECENTS_MAX = 8;

function loadRecents() {
    try {
        const raw = localStorage.getItem(RECENTS_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
}

function saveRecents(list) {
    try { localStorage.setItem(RECENTS_KEY, JSON.stringify(list.slice(0, RECENTS_MAX))); } catch {}
}

function pushRecent(entry) {
    const list = loadRecents().filter(r => (r.path || r.id) !== (entry.path || entry.id));
    list.unshift({
        kind: entry.kind || 'page',
        id: entry.id,
        label: entry.label,
        sub: entry.sub || '',
        wsId: entry.wsId,
        path: entry.path,
        handler: entry.handler,
    });
    saveRecents(list);
    return list;
}

function TileFor({ wsId, kind, workspaces }) {
    const ws = wsId && workspaces ? workspaces.find(w => w.id === wsId) : null;
    if (ws && ws.glyph) {
        const G = ws.glyph;
        return (
            <div className="cmd-item-tile" style={{ background: ws.hue }} aria-hidden="true">
                <G size={14} strokeWidth={2} color="#fff" />
            </div>
        );
    }
    return (
        <div className="cmd-item-tile" aria-hidden="true">
            {kind === 'action' ? '⚡' : '•'}
        </div>
    );
}

function HighlightedTitle({ label, query }) {
    if (!query) return <>{label}</>;
    const h = highlight(label, query);
    if (typeof h === 'string') return <>{h}</>;
    return (<>{h.before}<mark>{h.match}</mark>{h.after}</>);
}

const KIND_LABEL = {
    page: 'Page',
    action: 'Action',
    contact: 'Contact',
    company: 'Company',
    deal: 'Deal',
    record: 'Record',
};

export function CommandPalette({ onClose, onOpenHub }) {
    const [query, setQuery] = useState('');
    const [records, setRecords] = useState(null);
    const [recents, setRecents] = useState(() => loadRecents());
    const [selectedKey, setSelectedKey] = useState(null);
    const [askSelected, setAskSelected] = useState(false);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    const dialogRef = useRef(null);
    const previouslyFocused = useRef(null);
    const navigate = useNavigate();
    const { setCurrentWorkspace, visibleWorkspaces } = useWorkspace();
    const { getAuthHeader } = useAuth();
    const { definition: pipelineDefinition } = usePipelineDefinition('sales');
    const { toggleTheme } = useTheme();

    // Focus + focus-return
    useEffect(() => {
        previouslyFocused.current = document.activeElement;
        inputRef.current?.focus();
        return () => {
            if (previouslyFocused.current && typeof previouslyFocused.current.focus === 'function') {
                previouslyFocused.current.focus();
            }
        };
    }, []);

    // Fetch records once per open (aborted on close)
    useEffect(() => {
        const ctrl = new AbortController();
        const headers = getAuthHeader();
        if (!headers.Authorization) { setRecords([]); return; }
        (async () => {
            try {
                const [c, a, d] = await Promise.allSettled([
                    axios.get(`${API}/contacts`, { headers, signal: ctrl.signal }),
                    axios.get(`${API}/accounts`, { headers, signal: ctrl.signal }),
                    axios.get(`${API}/deals`, { headers, signal: ctrl.signal }),
                ]);
                const list = (r) => r.status !== 'fulfilled' ? []
                    : Array.isArray(r.value.data) ? r.value.data : (r.value.data?.data || []);
                const contacts = list(c).slice(0, 200).map(x => ({
                    kind: 'contact',
                    id: x.contact_id || x.id,
                    label: `${x.first_name || ''} ${x.last_name || ''}`.trim() || x.email || 'Contact',
                    sub: x.company_name || x.email || 'Contact',
                    path: `/contacts/${x.contact_id || x.id}`,
                    wsId: 'crm-core',
                }));
                const companies = list(a).slice(0, 200).map(x => ({
                    kind: 'company',
                    id: x.account_id || x.id,
                    label: x.name || 'Company',
                    sub: 'Company',
                    path: `/companies/${x.account_id || x.id}`,
                    wsId: 'crm-core',
                }));
                const deals = list(d).slice(0, 200).map(x => ({
                    kind: 'deal',
                    id: x.deal_id || x.id,
                    label: x.title || x.name || 'Deal',
                    stage: x.stage,
                    path: `/deals/${x.deal_id || x.id}`,
                    wsId: 'crm-core',
                }));
                setRecords([...contacts, ...companies, ...deals]);
            } catch {
                setRecords([]);
            }
        })();
        return () => ctrl.abort();
    }, [getAuthHeader]);

    // Deal rows keep the stored stage; its label comes from the org's Sales definition (#885),
    // which can resolve after the records do.
    const labelledRecords = useMemo(() => (records || []).map((r) => (r.kind === 'deal'
        ? { ...r, sub: `Deal${r.stage ? ' · ' + dealStageMeta(pipelineDefinition, r.stage).label : ''}` }
        : r)), [records, pipelineDefinition]);

    // Recompute result set from query + inputs
    const { rows, groups } = useMemo(() => {
        const allItems = getAllSidebarItems(visibleWorkspaces);
        const activeWs = getActiveWorkspaces(visibleWorkspaces);
        const pages = searchPages(query, allItems);
        const actions = searchActions(query, activeWs);
        const recs = searchRecords(query, labelledRecords);
        return buildResults({ query, pages, actions, records: recs, recents });
    }, [query, labelledRecords, recents, visibleWorkspaces]);

    // Ensure a valid selection whenever the row list changes
    useEffect(() => {
        if (rows.length === 0) {
            setSelectedKey(null);
            return;
        }
        if (!selectedKey || !rows.some(r => r.key === selectedKey)) {
            setSelectedKey(rows[0].key);
            setAskSelected(false);
        }
    }, [rows, selectedKey]);

    const showAskRow = query.trim().length > 1;
    const askSub = 'answers grounded in your CRM data';

    const executeRow = useCallback((row) => {
        if (!row) return;
        if (row.kind === 'action') {
            if (row.handler === 'open-hub') {
                onClose();
                if (typeof onOpenHub === 'function') onOpenHub();
            } else if (row.handler === 'toggle-theme') {
                toggleTheme();
                onClose();
            } else if (row.path) {
                setCurrentWorkspace(row.wsId);
                onClose();
                navigate(row.path);
            }
        } else {
            if (row.wsId) setCurrentWorkspace(row.wsId);
            if (row.path) {
                onClose();
                navigate(row.path);
            }
        }
        setRecents(pushRecent(row));
    }, [navigate, onClose, onOpenHub, setCurrentWorkspace, toggleTheme]);

    const executeAsk = useCallback(() => {
        const prompt = query.trim();
        if (!prompt) return;
        onClose();
        navigate('/ai-assistant', { state: { prompt } });
        setRecents(pushRecent({
            kind: 'action',
            id: 'ask-ai',
            label: `Ask AI: ${prompt.slice(0, 40)}${prompt.length > 40 ? '…' : ''}`,
            sub: askSub,
            path: '/ai-assistant',
            handler: 'ask-ai',
        }));
    }, [query, onClose, navigate]);

    const currentIndex = useMemo(() => {
        if (!selectedKey) return -1;
        return rows.findIndex(r => r.key === selectedKey);
    }, [rows, selectedKey]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            if (!rows.length) return;
            const next = Math.min(currentIndex + 1, rows.length - 1);
            setSelectedKey(rows[Math.max(next, 0)].key);
            setAskSelected(false);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            if (!rows.length) return;
            const next = Math.max(currentIndex - 1, 0);
            setSelectedKey(rows[next].key);
            setAskSelected(false);
        } else if (e.key === 'Tab') {
            if (!showAskRow) return;
            e.preventDefault();
            setAskSelected(prev => !prev);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (askSelected && showAskRow) { executeAsk(); return; }
            const row = rows.find(r => r.key === selectedKey) || rows[0];
            executeRow(row);
        }
    }, [rows, currentIndex, askSelected, showAskRow, executeAsk, executeRow, selectedKey]);

    // Focus trap
    useEffect(() => {
        const onKey = (e) => {
            if (e.key !== 'Tab' || !dialogRef.current) return;
            // For Tab, we let the palette's own handler above steal it when Ask-AI is visible.
            if (showAskRow) return;
            const focusables = dialogRef.current.querySelectorAll('input, button, [tabindex]:not([tabindex="-1"])');
            if (!focusables.length) return;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [showAskRow]);

    // Scroll selected row into view
    useEffect(() => {
        if (!selectedKey || !listRef.current) return;
        const el = listRef.current.querySelector(`[data-key="${CSS.escape(selectedKey)}"]`);
        if (el?.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
    }, [selectedKey]);

    const trimmed = query.trim();

    return (
        <div className="cmd-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div
                ref={dialogRef}
                className="cmd-palette"
                role="dialog"
                aria-modal="true"
                aria-label="Command palette"
            >
                <div className="cmd-input-wrap">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                        <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
                        <path d="M12.5 12.5L16 16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <input
                        ref={inputRef}
                        type="text"
                        className="cmd-input"
                        placeholder="Search or jump to…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={handleKeyDown}
                        data-testid="command-palette-input"
                    />
                </div>

                {showAskRow && (
                    <div
                        className={`cmd-ask-row ${askSelected ? 'selected' : ''}`}
                        onClick={executeAsk}
                        role="button"
                        tabIndex={-1}
                        data-testid="cmd-ask-ai"
                    >
                        <div className="cmd-ask-orb" aria-hidden="true" />
                        <div className="cmd-item-text">
                            <div className="cmd-ask-title">Ask AI: “{trimmed}”</div>
                            <div className="cmd-ask-sub">{askSub}</div>
                        </div>
                        <span className="cmd-item-kind">Tab ↵</span>
                    </div>
                )}

                <div ref={listRef} className="cmd-results">
                    {rows.length === 0 && (
                        <div className="cmd-empty">
                            {records === null ? 'Loading records…' : 'No results found'}
                        </div>
                    )}
                    {rows.map((row, index) => {
                        const groupHere = groups.find(g => g.at === index);
                        const isSelected = row.key === selectedKey && !askSelected;
                        return (
                            <React.Fragment key={row.key}>
                                {groupHere && (
                                    <div className="cmd-group-label">{groupHere.label}</div>
                                )}
                                <div
                                    data-key={row.key}
                                    className={`cmd-item ${isSelected ? 'selected' : ''}`}
                                    onClick={() => executeRow(row)}
                                    data-testid={`cmd-result-${row.key}`}
                                >
                                    <TileFor wsId={row.wsId} kind={row.kind} workspaces={visibleWorkspaces} />
                                    <div className="cmd-item-text">
                                        <div className="cmd-item-title">
                                            <HighlightedTitle label={row.label} query={query} />
                                        </div>
                                        <div className="cmd-item-subtitle">{row.sub}</div>
                                    </div>
                                    <span className="cmd-item-kind">{KIND_LABEL[row.kind] || 'Result'}</span>
                                </div>
                            </React.Fragment>
                        );
                    })}
                </div>
                <div className="cmd-footer">
                    <span><span className="kbd">↑↓</span> navigate</span>
                    <span><span className="kbd">↵</span> open</span>
                    <span><span className="kbd">tab</span> ask AI</span>
                    <span><span className="kbd">esc</span> close</span>
                </div>
            </div>
        </div>
    );
}

export default CommandPalette;
